import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'libs/acl/src/lib/auth.service';
import { first } from 'rxjs';

@Component({
  selector: 'firebird-web-auth-callback',
  templateUrl: './auth-callback.component.html',
  styleUrls: ['./auth-callback.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AuthCallbackComponent implements OnInit {
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(first()).subscribe((_params) => {
      try {
        if (this.authService.hasValidAccessToken()) {
          this.handleSuccessfulAuthentication();
        } else {
          // Attempt router navigation first; fallback to authService.login() if navigation rejects or returns false.
          this.router
            .navigate(['/login'])
            .then((navigated: boolean) => {
              if (!navigated) {
                this.authService.login();
              }
            })
            .catch(() => {
              this.authService.login();
            });
        }
      } catch (err) {
        console.error('Unhandled error in AuthCallbackComponent:', err);
        // expose a simple recovery action
        try {
          this.authService.recoverFromAuthError();
        } catch (e) {
          console.error('recoverFromAuthError failed:', e);
        }
      }
    });
  }

  // Exposed to the template to allow manual recovery if the page gets stuck
  public onRecover(): void {
    console.log('AuthCallback: manual recover triggered');
    try {
      this.authService.recoverFromAuthError();
    } catch (err) {
      console.error('AuthCallback: manual recover failed', err);
    }
  }

  private handleSuccessfulAuthentication(): void {
    const routeUrl = this.authService.getState();
    if (!routeUrl) {
      this.router.navigate(['/']);
      return;
    }

    let decoded: string;
    try {
      decoded = decodeURIComponent(routeUrl);
    } catch (e) {
      decoded = routeUrl;
    }

    // Special-case old blog/blurbs deep links which expect a full origin + path
    if (decoded.includes('blurbPostID') || decoded.includes('blogPostID')) {
      window.open(`${window.location.origin}${decoded}`, '_self');
      return;
    }

    // If the saved state is an absolute URL, handle same-origin vs external safely
    const isAbsolute = /^https?:\/\//i.test(decoded) || /^\/\//.test(decoded);
    if (isAbsolute) {
      const origin = window.location.origin;
      if (decoded.startsWith(origin)) {
        // Convert same-origin absolute URL to an app-relative path for the router
        const path = decoded.substring(origin.length) || '/';
        this.router.navigateByUrl(path);
        return;
      }

      this.router.navigate(['/']);
      return;
    }

    // Avoid navigating back to any auth callback route (prevents reload loop)
    const normalized = decoded || '/';
    if (normalized.startsWith('/auth/callback')) {
      console.warn(
        'AuthCallback: saved state points to auth callback — routing to root instead'
      );
      this.router.navigate(['/']);
      return;
    }

    // Otherwise assume an in-app relative path or query string
    this.router.navigateByUrl(normalized);
  }
}
